import GridTable from "@components/grid-table"
import { useCallLogsPageContext } from "@contexts/main/call_logs_page_context"
import { listCallAdminLogsQuery } from "@domain/call_logs"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material"
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams
} from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { formatDate } from "@utils/functions/formatDate"
import type { TFunction } from "i18next"
import { showErrorToast } from "@utils/toast"
import { useEffect, useState } from "react"
import {
  CallAdminLog,
  CallAdminLogType,
  GetCallAdminLogsInput
} from "__generated__/graphql"

/** Seconds -> m:ss (h:mm:ss past an hour), so talk time is readable at a glance. */
function formatDuration(totalSeconds?: number | null): string {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0))
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const mm = `${m}:${s.toString().padStart(2, "0")}`

  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : mm
}

type StatusChipColor = "default" | "success" | "warning" | "error" | "info"

/** One colour per disposition family, so a call that went wrong is spottable unread. */
function callStatusColor(status: string): StatusChipColor {
  switch (status.toUpperCase()) {
    case "ANSWERED":
      return "success"
    case "FAILED":
    case "UNREACHABLE":
      return "error"
    case "MISSED":
    case "NO_ANSWER":
    case "ABANDONED":
    case "IVR_ABANDONED":
    case "QUEUE_ABANDONED":
      return "warning"
    case "IN_QUEUE":
    case "IN_PROGRESS":
    case "RINGING":
    case "INITIATED":
      return "info"
    default:
      // declined / rejected / canceled / completed — nothing to flag. `COMPLETED` stays
      // neutral on purpose: it is Twilio saying the leg ended, not that anyone answered.
      return "default"
  }
}

/**
 * Both writers report upper-snake statuses. Locale entries cover the known set; an
 * unmapped one degrades to readable lower case instead of shouting the raw key.
 */
function callStatusLabel(
  status: string,
  translate: TFunction<"translation", undefined>
): string {
  const key = `call_status_${status.toLowerCase()}`
  const translated = translate(key)

  return translated && translated !== key
    ? translated
    : status.toLowerCase().replace(/_/g, " ")
}

interface Props {
  onSelectCall: (callSid: string) => void
}

export default function CallAdminLogList({ onSelectCall }: Props) {
  const { translate, filters } = useCallLogsPageContext()
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  })
  const [search, setSearch] = useState("")
  const [callType, setCallType] = useState<CallAdminLogType | "ALL">("ALL")
  const debouncedSearch = useDebounce(search, 500)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [debouncedSearch, callType, filters])

  const { data, isLoading } = useQuery({
    queryKey: [
      "call-logs-list",
      pagination.page,
      pagination.pageSize,
      filters,
      debouncedSearch,
      callType
    ],
    queryFn: async () => {
      const params: GetCallAdminLogsInput = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        customerId: filters.customerId || "",
        startDate: filters.startDate ? filters.startDate.getTime() : undefined,
        endDate: filters.endDate ? filters.endDate.getTime() : undefined,
        search: debouncedSearch,
        callType: callType === "ALL" ? undefined : callType
      }
      const response = await listCallAdminLogsQuery(params)
      if (!response.isSuccess || !response.data) {
        showErrorToast(
          response.errorMessage ??
            translate("error_fetching_logs") ??
            "Error fetching logs"
        )
        return { logs: [], total: 0 }
      }
      return response.data
    },
    enabled: !!filters.customerId || !!filters.startDate || !!filters.endDate
  })

  /**
   * The list is scanned left-to-right, so the row action and the fields that identify a
   * call come first; SID and money sit at the tail. Widths are explicit rather than
   * `flex` everywhere — leftover space spread across ten columns is what pushed the
   * values far apart.
   */
  const columns: GridColDef[] = [
    {
      field: "actions",
      headerName: translate("actions"),
      width: 84,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onSelectCall(params.row.callSid as string)}
        >
          {translate("view")}
        </Button>
      )
    },
    {
      field: "createdAt",
      headerName: translate("created_at"),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {formatDate(translate("date_format"), params.value as string)}
        </Typography>
      )
    },
    {
      field: "customerName",
      headerName: translate("customer_name"),
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {(params.row as CallAdminLog).customerName || "-"}
        </Typography>
      )
    },
    {
      field: "callType",
      headerName: translate("call_type"),
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const isTeam =
          (params.row as CallAdminLog).callType === CallAdminLogType.Team
        return (
          <Chip
            size="small"
            variant="outlined"
            color={isTeam ? "secondary" : "default"}
            label={
              isTeam
                ? translate("call_type_team")
                : translate("call_type_customer")
            }
          />
        )
      }
    },
    {
      // Customer calls identify the far end by phone number; team calls by the two
      // colleagues, so one column renders whichever the row actually has.
      field: "callerNumber",
      headerName: translate("call_participants"),
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as CallAdminLog
        if (row.callType !== CallAdminLogType.Team) {
          return (
            <Typography variant="body2" noWrap>
              {row.callerNumber || "-"}
            </Typography>
          )
        }

        return (
          <Typography variant="body2" noWrap>
            {row.callerName || "-"} → {row.calleeName || "-"}
          </Typography>
        )
      }
    },
    {
      field: "status",
      headerName: translate("status"),
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const status = (params.row as CallAdminLog).status
        if (!status) return <Box>-</Box>

        return (
          <Chip
            size="small"
            variant="outlined"
            color={callStatusColor(status)}
            label={callStatusLabel(status, translate)}
          />
        )
      }
    },
    {
      field: "direction",
      headerName: translate("direction"),
      width: 100
    },
    {
      field: "totalDuration",
      headerName: translate("total_duration"),
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Box>{formatDuration((params.row as CallAdminLog).totalDuration)}</Box>
      )
    },
    {
      field: "totalCost",
      headerName: translate("total_cost"),
      width: 100
    },
    {
      // A truncated SID is useless — it is the key you paste into /call-debug.
      field: "callSid",
      headerName: translate("call_sid"),
      width: 280,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: 12 }}
        >
          {(params.row as CallAdminLog).callSid || "-"}
        </Typography>
      )
    }
  ]

  return (
    <Stack sx={{ height: "100%", p: 2, overflow: "hidden", gap: 2 }}>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={callType}
          onChange={(_event, value: CallAdminLogType | "ALL" | null) => {
            if (value) setCallType(value)
          }}
        >
          <ToggleButton value="ALL">{translate("all")}</ToggleButton>
          <ToggleButton value="CUSTOMER">
            {translate("call_type_customer")}
          </ToggleButton>
          <ToggleButton value="TEAM">
            {translate("call_type_team")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={translate("search") || "Search..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: "background.paper"
            }
          }}
        />
      </Box>
      {/* DataGrid's default 10px-a-side cell padding is what makes ten columns read as
          islands; halving it pulls the values together without touching the shared table. */}
      <Box
        sx={{
          "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
            px: 0.75
          }
        }}
      >
        <GridTable
          loading={isLoading}
          rows={data?.logs || []}
          columns={columns}
          rowCount={data?.total || 0}
          defaultPageModel={pagination}
          onPaginationModelChange={setPagination}
          getRowId={(row: CallAdminLog) => row._id}
        />
      </Box>
    </Stack>
  )
}
